import DateOnlyRenderer from '@customer/components/tables/cells/date/DateOnlyRenderer';
import { columnDefPartnerResourceDownload } from '@customer/components/tables/grid/columnDefs';
import DocumentTypeCellRenderer from '@cas/components/tables/cells/document-type/DocumentTypeCellRenderer';
import { FilterGroup } from '@customer/filters/types';
import useFilters from '@customer/filters/useFilters';
import { useTableData } from '@customer/shared/hooks/useTableData';
import {
  PartnerResourcesQueryParams,
  useGetPartnerResourcesQuery,
} from '@customer/store/apis/documentsApi';
import { useAppDispatch } from '@customer/store/hooks';
import { openSidePanel, SidePanel } from '@customer/store/slices/globalSlice';
import { ColDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PartnerResourceModel } from '@cas/config/documents';

export const usePartnerResources = (noDocDesc: string) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const filters = useFilters<PartnerResourcesQueryParams>(FilterGroup.PartnerResources);

  const { data, ...requestRemainder } = useGetPartnerResourcesQuery(filters, {
    refetchOnMountOrArgChange: 600,
  });

  const columns = useMemo<ColDef<PartnerResourceModel>[]>(() => {
    return [
      {
        field: 'date',
        headerName: t('Date'),
        cellRenderer: DateOnlyRenderer,
        maxWidth: 140,
        sort: 'desc',
      },
      {
        field: 'documentType',
        headerName: t('ResourceTypeTitle'),
        maxWidth: 180,
        cellRenderer: DocumentTypeCellRenderer,
      },
      { field: 'fileType', headerName: t('FileType'), maxWidth: 120 },
      { field: 'title', headerName: t('Title') },
      { ...columnDefPartnerResourceDownload(t), maxWidth: 150 },

      // Allow the description field to be searchable (combined with includeHiddenColumnsInQuickFilter = true)
      { headerName: 'desc', field: 'description', hide: true },
    ];
  }, [t]);

  const noResultsDescription = t(noDocDesc);

  const { hasData, noDataDescription } = useTableData<PartnerResourceModel>({
    data: data?.items,
    ...requestRemainder,
    noResultsDescription,
  });

  const onRowClick = useCallback(
    (data: PartnerResourceModel) => {
      dispatch(
        openSidePanel({
          sidePanel: SidePanel.PartnerResource,
          modelId: data.id.toString(),
        }),
      );
    },
    [dispatch],
  );

  return {
    columns,
    data,
    hasData,
    noDataDescription,
    noResultsDescription,
    onRowClick,
  };
};

export default usePartnerResources;
